import { includes, map } from 'lodash';
import { useEffect, ChangeEvent, FocusEvent } from 'react';
import styled from 'styled-components/macro';
import { ReactComponent as ChevronDown } from '../../../images/icons/chevron-down.svg';
import { spacing05 as caretSize, spacing07 } from '../../../styling/spacing';
import { ValidityProps } from '../FormField';
import { inputStyles } from './inputStyles';

export const selectTestId = 'select';

export type SelectOption<TValue> = {
  value: TValue;
  text: string;
};

export const getNullSelectOption = <TValue,>(): SelectOption<TValue | null> => ({
  text: '—',
  value: null,
});

export type SelectOptions<TValue> = Array<SelectOption<TValue>>;

type SelectProps<TValue> = {
  options: Array<SelectOption<TValue>>;
  onChange: (newValue: TValue) => void;
  onBlur?: (event: FocusEvent) => void;
  value: TValue;
  name?: string;
  disabled?: boolean;
  mapValueToKey?: (value: TValue) => string;
  isSmall?: boolean;
} & ValidityProps;

export const Select = <TValue,>({
  options,
  onChange,
  onBlur,
  name,
  $isValid,
  $isInvalid,
  $hasWarning,
  isSmall,
  disabled,
  mapValueToKey = value => (typeof value === 'object' ? JSON.stringify(value) : `${value}`),
  ...props
}: SelectProps<TValue>) => {
  const optionsByKey = getOptionsByKey(options, mapValueToKey);
  const currentKey = mapValueToKey(props.value);

  const onChangeWrapper = (event: ChangeEvent<HTMLSelectElement>) => {
    const newOptionKey = event.target.value;
    const newOption = optionsByKey[newOptionKey];
    onChange(newOption?.value);
  };

  useEffect(() => {
    const validValues = options.map(option => option.value);

    if (!includes(validValues, props.value) && includes(validValues, null)) {
      // Clear the value on the <Select /> if an invalid value has somehow been selected and `null` is a valid option
      onChange(null as TValue);
    }
  }, [props.value, options]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SelectContainer>
      <SelectBox
        name={name}
        onChange={onChangeWrapper}
        onBlur={onBlur}
        $isValid={$isValid}
        $isInvalid={$isInvalid}
        $hasWarning={$hasWarning}
        isSmall={isSmall}
        data-testid={selectTestId}
        value={currentKey}
        disabled={disabled}
      >
        {map(options, ({ value, text }: SelectOption<TValue>) => {
          const key = mapValueToKey(value);
          return (
            <Option value={key} key={key}>
              {text}
            </Option>
          );
        })}
      </SelectBox>
      <SelectCaret />
    </SelectContainer>
  );
};

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const selectBoxPaddingRight = spacing07;

const SelectCaret = styled(ChevronDown)`
  position: absolute;
  height: ${caretSize};
  width: ${caretSize};
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.6;
`;

const SelectBox = styled.select<ValidityProps & { isSmall?: boolean }>`
  ${props =>
    inputStyles({
      $isValid: props.$isValid,
      $isInvalid: props.$isInvalid,
      $hasWarning: props.$hasWarning,
      $isSmall: props.isSmall,
    })};

  cursor: pointer;
  padding-right: ${selectBoxPaddingRight};
  width: 100%;
  max-height: 48px;

  // Remove default dropdown caret
  -webkit-appearance: none;
  -moz-appearance: none;
`;

const Option = styled.option`
  background-color: white;
`;

type SelectOptionsByKey<TValue> = {
  [key: string]: SelectOption<TValue>;
};

export const getOptionsByKey = <TValue,>(options: SelectOptions<TValue>, mapValueToKey: (value: TValue) => string) => {
  const optionsByKey: SelectOptionsByKey<TValue> = {};
  map(options, (option: SelectOption<TValue>) => {
    optionsByKey[mapValueToKey(option.value)] = option;
  });
  return optionsByKey;
};
